import React, { useEffect, useRef, useState } from "react";
import { BlockStack, List, Text } from "@shopify/polaris";
import type { PurchaseOrderLineItems } from "../../../api_utils/types";
import { commaDelimiter, formattedVariantTitle } from "../../../helper_functions/utils";
import ConfirmationDialog from "../../common/ConfirmationDialog";

export function useReceivedVariantRemoval(
  purchaseOrderLineItems: PurchaseOrderLineItems,
  removeVariants: (horseVariantIds: number[]) => void,
  isLoading: boolean,
): {
  removeVariant: (horseVariantId: number, activator: HTMLDivElement | null) => void;
  removalWarning: (horseVariantIds: number[]) => React.ReactNode;
  removalConfirmation: React.JSX.Element;
} {
  const [variantIdsToRemove, setVariantIdsToRemove] = useState<number[]>([]);
  const removalActivator = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isLoading) setVariantIdsToRemove([]);
  }, [isLoading]);

  const removeVariant = (horseVariantId: number, activator: HTMLDivElement | null): void => {
    if (purchaseOrderLineItems[horseVariantId].received > 0) {
      removalActivator.current = activator;
      setVariantIdsToRemove([horseVariantId]);
    } else {
      removeVariants([horseVariantId]);
    }
  };

  const confirmRemoval = (): void => {
    if (isLoading) return;
    removeVariants(variantIdsToRemove);
    closeRemovalConfirmation();
  };

  const closeRemovalConfirmation = (): void => { setVariantIdsToRemove([]); };

  const removalWarning = (horseVariantIds: number[]): React.ReactNode => {
    const removedIds = new Set(horseVariantIds);
    const receivedLineItems = Object.values(purchaseOrderLineItems).filter(
      (purchaseOrderLineItem) => removedIds.has(purchaseOrderLineItem.horse_variant_id) && purchaseOrderLineItem.received > 0,
    );
    if (receivedLineItems.length === 0) return null;

    return (
      <BlockStack gap="400">
        <Text as="p">
          Inventory has already been received for {receivedLineItems.length > 1 ? `${commaDelimiter(receivedLineItems.length)} variants` : "the following variant"}. Removing a variant does not undo receiving or change available inventory.
        </Text>
        {receivedLineItems.length === 1 && (
          <List>
            {receivedLineItems.map((purchaseOrderLineItem) => (
              <List.Item key={purchaseOrderLineItem.horse_variant_id}>
                {[purchaseOrderLineItem.product_title, formattedVariantTitle(purchaseOrderLineItem.variant_title), purchaseOrderLineItem.sku].filter(Boolean).join(" / ")}: {commaDelimiter(purchaseOrderLineItem.received)} received
              </List.Item>
            ))}
          </List>
        )}
      </BlockStack>
    );
  };

  return {
    removeVariant,
    removalWarning,
    removalConfirmation: (
      <ConfirmationDialog
        activator={removalActivator}
        active={variantIdsToRemove.length > 0}
        content={variantIdsToRemove.length > 0 && removalWarning(variantIdsToRemove)}
        isDestructive
        okText="Remove variant"
        onCancel={closeRemovalConfirmation}
        onClose={closeRemovalConfirmation}
        onOk={confirmRemoval}
        title="Remove variant?"
      />
    ),
  };
}
